// @flow

import { getAuth0Client } from '../services/auth0';
import history from '../root/history';
import { LOGOUT } from '../constants/routes';
import { errorToast } from '../utils/toaster';
import { AUTH_SERVICE_API_BASE } from '../config/vars';

type callArgs = {
  path: string,
  fetchOptions?: Object,
  otherOptions?: Object,
  responseExpected?: boolean,
  responseMethod?: 'json' | 'blob'
};

// TODO: combine this `call` method and use it in all the *api.js services files
const call = async ({
  path,
  fetchOptions,
  otherOptions = {},
  responseExpected = true,
  responseMethod = 'json'
}: callArgs): Promise<any> => {
  const fetchOpts = {
    ...fetchOptions,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: ''
    }
  };

  const auth0Client = getAuth0Client();
  if (auth0Client) {
    const token = await auth0Client.getTokenSilently();

    if (token && !otherOptions.dontAddAuthorization) {
      fetchOpts.headers.Authorization = `Bearer ${token}`;
    }
  }

  const res = await fetch(
    (AUTH_SERVICE_API_BASE + path: string),
    (fetchOpts: Object)
  );

  if (res.ok && responseExpected) {
    // $FlowFixMe Flow doesnt know if a response can have a json or blob method
    return await res[responseMethod]();
  } else if (res.ok && !responseExpected) {
    return res;
  }

  // if response is 401 show a toast prompting the user to log back in
  if (res.status === 401 && !otherOptions.disable401Toast) {
    errorToast({
      message: 'Couldn\'t authenticate. You probably need to log back in',
      action: {
        text: 'Log in',
        onClick: e => history.push(LOGOUT)
      },
      timeout: 0
    });
    const responseBody = await res.json();
    console.error(responseBody);
  }

  const error: { response?: Object } = new Error(res.statusText);
  error.response = res;
  throw error;
};

type GetUsersOptions = {
  orgId: string,
  status: 'normal' | 'invited' | 'adopted',
  include?: Array<'preferences' | 'roles'>,
  pageSize?: number,
};

export const getUsers = ({
  orgId,
  status,
  include,
  pageSize = 300,
}: GetUsersOptions) => {
  const isAdopted = status === 'adopted';
  const queryStatus = status === 'invited' ? 'created' : 'active';
  const route = isAdopted ? '/api/v1/adopted-users' : '/api/v1/users';
  const includePart =
    include && include.length
      ? `&include=${encodeURIComponent(include.join(','))}`
      : '';
  const path = `${route}?pageSize=${pageSize}&query[ownerOrgId]=${encodeURIComponent(
    orgId,
  )}&query[status]=${queryStatus}${includePart}`;
  const fetchOptions = { method: 'GET' };

  return call({ path, fetchOptions });
};

export const getActiveUsers = (orgId: string) =>
  getUsers({ orgId, status: 'normal' });

export const getRulesForUser = (userId: string, orgId: string) => {
  const path = `/api/v1/users/${userId}/rules?query[type]=location&query[orgId]=${orgId}`;
  const fetchOptions = { method: 'GET' };

  return call({ path, fetchOptions });
};

export const getInvitedUsers = (orgId: string) =>
  getUsers({ orgId, status: 'invited' });

export const getAdoptedUsers = (orgId: string) =>
  getUsers({ orgId, status: 'adopted' });

export const inviteUser = (
  orgName: string,
  email: string,
  roles: number[],
  orgToInviteTo: string
) => {
  const path = '/api/v1/invite-user';

  const fetchOptions = {
    method: 'POST',
    body: JSON.stringify({
      invitingOrgName: orgName,
      email,
      roles,
      orgToInviteTo
    })
  };

  return call({ path, fetchOptions, responseExpected: false });
};

// The server defaults to a page size of 30 and no pagination is followed
// here, so ask for a ceiling comfortably above the catalogue's size.
export const getAllRoles = () => {
  const path = '/api/v1/roles?pageSize=100';
  const fetchOptions = { method: 'GET' };

  return call({ path, fetchOptions });
};

export const deleteUser = (userId: number) => {
  const path = `/api/v1/users/${userId}`;

  const fetchOptions = {
    method: 'DELETE'
  };

  return call({ path, fetchOptions, responseExpected: false });
};

export const deleteAdoptedUser = (userId: number, orgId: string) => {
  const path = `/api/v1/users/${userId}/organisations/${orgId}`;

  const fetchOptions = {
    method: 'DELETE'
  };

  return call({ path, fetchOptions, responseExpected: false });
};

export const createUserRole = (
  userId: number,
  roleId: number,
  orgId: string
) => {
  const path = `/api/v1/users/${userId}/roles`;

  const fetchOptions = {
    method: 'POST',
    body: JSON.stringify({ orgId, roleId })
  };

  return call({ path, fetchOptions });
};

export const deleteUserRole = (
  userId: number,
  roleId: number,
  orgId: string
) => {
  const path = `/api/v1/users/${userId}/roles/${roleId}?query[orgId]=${orgId}`;

  const fetchOptions = {
    method: 'DELETE'
  };

  return call({ path, fetchOptions, responseExpected: false });
};

export const user = {
  update: (userId: string, updatedUser: Object) => {
    const body = JSON.stringify(updatedUser);
    return call({
      path: `/api/v1/users/${userId}`,
      fetchOptions: { method: 'PUT', body }
    });
  }
};

type PreferenceCreateBody = {
  orgId: string,
  preferenceType: 'location' | 'team_type' | 'tag',
  preferenceValue: string,
};

export const createUserPreference = (
  userId: number,
  body: PreferenceCreateBody
) =>
  call({
    path: `/api/v1/users/${userId}/preferences`,
    fetchOptions: { method: 'POST', body: JSON.stringify(body) }
  });

export const updateUserPreference = (
  userId: number,
  preferenceId: number,
  body: { preferenceValue: string }
) =>
  call({
    path: `/api/v1/users/${userId}/preferences/${preferenceId}`,
    fetchOptions: { method: 'PUT', body: JSON.stringify(body) }
  });

export const deleteUserPreference = (
  userId: number,
  preferenceId: number
) =>
  call({
    path: `/api/v1/users/${userId}/preferences/${preferenceId}`,
    fetchOptions: { method: 'DELETE' },
    responseExpected: false
  });

type BulkPreferencesBody = {
  orgId: string,
  userIds: number[],
  preferences: Array<{ preferenceType: string, preferenceValue: string }>,
};

export const bulkUpsertUserPreferences = (body: BulkPreferencesBody) =>
  call({
    path: `/api/v1/users/preferences/bulk`,
    fetchOptions: { method: 'PUT', body: JSON.stringify(body) }
  });

export const bulkDeleteUserPreferences = (body: BulkPreferencesBody) =>
  call({
    path: `/api/v1/users/preferences/bulk`,
    fetchOptions: { method: 'DELETE', body: JSON.stringify(body) }
  });
